import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-all duration-300",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-slate-300 bg-white/80 backdrop-blur-sm text-slate-700 hover:bg-slate-100 hover:border-cyan-500/50 transition-all duration-300",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-slate-100 text-slate-700 hover:text-slate-900",
        link: "text-cyan-600 underline-offset-4 hover:underline hover:text-cyan-700",
        gradient: "bg-gradient-to-r from-hibane-ocean to-hibane-sky text-white hover:opacity-90 shadow-lg shadow-hibane-ocean/30 hover:shadow-hibane-sky/40 transition-all duration-300 hover:scale-105",
        bright: "bg-accent-yellow text-white hover:bg-accent-yellow/90 shadow-lg shadow-accent-yellow/25 hover:shadow-accent-yellow/40 transition-all duration-300",
        coral: "bg-accent-coral text-white hover:bg-accent-coral/90 shadow-lg shadow-accent-coral/25 hover:shadow-accent-coral/40 transition-all duration-300",
        ocean: "bg-hibane-ocean text-white hover:bg-hibane-ocean/90 shadow-lg shadow-hibane-ocean/25 hover:shadow-hibane-ocean/40 transition-all duration-300",
        // 3D Glass Effect Buttons - Solid Dark Blue with White Text
        "glass-3d": "bg-[#0f172a] backdrop-blur-xl border border-cyan-500/40 text-white font-semibold shadow-[0_8px_32px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.1)] hover:shadow-[0_16px_48px_rgba(34,211,238,0.25),inset_0_1px_0_rgba(255,255,255,0.15)] hover:bg-[#1e293b] hover:border-cyan-400/60 hover:text-white/90 hover:-translate-y-1 active:translate-y-0 transition-all duration-300",
        "glass-3d-primary": "bg-[#0f172a] backdrop-blur-xl border border-cyan-500/50 text-white font-bold shadow-[0_12px_40px_rgba(0,0,0,0.35),inset_0_2px_0_rgba(255,255,255,0.15)] hover:shadow-[0_20px_60px_rgba(34,211,238,0.3),inset_0_2px_0_rgba(255,255,255,0.2)] hover:bg-[#1e293b] hover:border-cyan-400/70 hover:text-white/90 hover:-translate-y-2 hover:scale-105 active:translate-y-0 active:scale-100 transition-all duration-300",
        "glass-3d-outline": "bg-[#0f172a] backdrop-blur-xl border-2 border-cyan-500/50 text-white shadow-[0_8px_32px_rgba(0,0,0,0.2),inset_0_1px_0_rgba(255,255,255,0.1)] hover:shadow-[0_16px_48px_rgba(34,211,238,0.2),inset_0_1px_0_rgba(255,255,255,0.15)] hover:bg-[#1e293b] hover:border-cyan-400/70 hover:text-white/90 hover:-translate-y-1 active:translate-y-0 transition-all duration-300",
        // Unified Hibane Theme Buttons (updated with solid dark blue and white text)
        "hibane-primary": "bg-[#0f172a] backdrop-blur-xl border border-cyan-500/50 text-white font-semibold shadow-[0_8px_32px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.1)] hover:shadow-[0_16px_48px_rgba(34,211,238,0.25),inset_0_1px_0_rgba(255,255,255,0.15)] hover:bg-[#1e293b] hover:border-cyan-400/60 hover:text-white/90 hover:-translate-y-1 active:translate-y-0 transition-all duration-300",
        "hibane-outline": "bg-[#0f172a] backdrop-blur-xl border border-cyan-500/40 text-white hover:bg-[#1e293b] hover:border-cyan-400/60 hover:text-white/90 shadow-[0_8px_32px_rgba(0,0,0,0.2),inset_0_1px_0_rgba(255,255,255,0.1)] hover:shadow-[0_16px_48px_rgba(34,211,238,0.15)] hover:-translate-y-1 active:translate-y-0 transition-all duration-300",
        "hibane-ghost": "text-slate-600 hover:text-cyan-600 hover:bg-cyan-50 transition-all duration-300",
        // ===== Fintech Template variants =====
        // Use these for the new clean fintech look (pill, brand-teal accent).
        fintech:
          "rounded-pill bg-brand-teal text-white font-semibold shadow-pill hover:brightness-110 active:brightness-95 transition-all duration-200 ease-fintech",
        "fintech-ghost":
          "rounded-pill bg-surface-muted text-ink hover:bg-surface-muted/70 font-medium transition-all duration-200 ease-fintech",
        "fintech-outline":
          "rounded-pill border border-border bg-surface text-ink hover:bg-surface-muted font-medium transition-all duration-200 ease-fintech",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
